/**
 * PersonalakteOverview.jsx
 *
 * Kompakte Übersichtskarte am Ende der Mitarbeiter-Detailseite.
 * Aggregiert den Status aller Akte-Objekte an einem Ort.
 * Nutzt nur bereits geladene Daten — kein neuer Backend-Call.
 *
 * Vorbereitet für spätere zentrale /admin/personalakten-Seite:
 * gleiche Komponente, andere Datenquelle.
 */
import React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  CheckCircle2, AlertTriangle, Clock, Minus,
  FileText, Briefcase, Shield, Award, AlertOctagon, Scale, Folder,
  ChevronRight,
} from 'lucide-react';

// ── Status-Typen ─────────────────────────────────────────────────────────────
const S = {
  OK:      { icon: CheckCircle2, color: 'text-green-600',  bg: 'bg-green-50  dark:bg-green-900/20',  border: 'border-green-200  dark:border-green-700',  label: (l) => l },
  WARN:    { icon: AlertTriangle,color: 'text-amber-600',  bg: 'bg-amber-50  dark:bg-amber-900/20',  border: 'border-amber-200  dark:border-amber-700',  label: (l) => l },
  PENDING: { icon: Clock,        color: 'text-blue-500',   bg: 'bg-blue-50   dark:bg-blue-900/20',   border: 'border-blue-200   dark:border-blue-700',   label: (l) => l },
  MISSING: { icon: AlertTriangle,color: 'text-red-500',    bg: 'bg-red-50    dark:bg-red-900/10',    border: 'border-red-200    dark:border-red-700',    label: (l) => l },
  NEUTRAL: { icon: Minus,        color: 'text-muted-foreground', bg: 'bg-muted/30', border: 'border-border', label: (l) => l },
};

function AkteRow({ icon: Icon, iconColor, label, status, badge, sectionKey, onOpenSection }) {
  const st = S[status] ?? S.NEUTRAL;
  const StatusIcon = st.icon;
  return (
    <button
      type="button"
      onClick={() => sectionKey && onOpenSection?.(sectionKey)}
      className={`w-full flex items-center gap-3 p-2.5 rounded-xl border text-left transition-colors
        ${st.bg} ${st.border}
        ${sectionKey ? 'cursor-pointer hover:brightness-95' : 'cursor-default'}`}
      data-testid={`personalakte-row-${sectionKey}`}>
      {/* Dokument-Icon */}
      <div className={`shrink-0 ${iconColor}`}>
        <Icon className="h-4 w-4" />
      </div>
      {/* Label */}
      <span className="flex-1 text-sm font-medium text-foreground min-w-0 truncate">{label}</span>
      {/* Status-Badge */}
      <span className={`flex items-center gap-1 text-xs font-semibold shrink-0 ${st.color}`}>
        <StatusIcon className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{badge}</span>
      </span>
      {/* Pfeil wenn anklickbar */}
      {sectionKey && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />}
    </button>
  );
}

export function PersonalakteOverview({
  bogen, signing, rvSigning, akteStatus, zeugnisse, abmahnungen, kuendigungen,
  employeeId, onOpenSection, onCreateContract,
}) {
  const isMinijob =
    bogen?.arbeitgeber?.arbeitszeit === 'minijob' ||
    bogen?.arbeitgeber?.beschaeftigungsart === 'minijob';

  // ── Dokumente-Zusammenfassung (eigener Mini-Fetch) ────────────────────────
  const [dokSummary, setDokSummary] = useState(null);
  useEffect(() => {
    if (!employeeId) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employee-documents/by-employee/${employeeId}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    ).then(r => {
      const docs = r.data?.documents || [];
      setDokSummary({ total: docs.length, pending: docs.filter(d => d.require_signature && !d.confirmed_at).length });
    }).catch(() => {}); // optional, kein Fehler anzeigen
  }, [employeeId]);

  // ── Personalbogen ─────────────────────────────────────────────────────────
  const bogenStatus = !bogen
    ? { s: 'MISSING', badge: 'fehlt' }
    : bogen.status === 'approved' || bogen.status === 'admin_filled'
    ? { s: 'OK',      badge: 'vollständig' }
    : bogen.status === 'submitted'
    ? { s: 'PENDING', badge: 'eingereicht' }
    : { s: 'WARN',    badge: 'Entwurf' };

  // ── Arbeitsvertrag ────────────────────────────────────────────────────────
  const vertragStatus = !signing
    ? { s: 'WARN',    badge: 'fehlt' }
    : signing.status === 'complete'
    ? { s: 'OK',      badge: 'unterschrieben' }
    : signing.status === 'signed_ag'
    ? { s: 'PENDING', badge: 'AN ausstehend' }
    : signing.status === 'signed_an'
    ? { s: 'PENDING', badge: 'AG ausstehend' }
    : { s: 'PENDING', badge: 'ausstehend' };

  // ── RV-Befreiungsantrag – KONSOLIDIERT (Personalbogen UND Vertrags-Signing) ──
  // Quelle: Backend /akte-status (führend). Fallback auf alte rvSigning-Prop nur wenn
  // kein konsolidierter Status geladen wurde. Gibt NIE 'fehlt' bei gültigem Beleg.
  const rvC = akteStatus?.rv;
  const rvStatus = rvC
    ? (!rvC.rv_relevant
        ? { s: 'NEUTRAL', badge: 'nicht erforderlich' }
        : rvC.state === 'befreiung_complete'
        ? { s: 'OK',      badge: rvC.needs_review ? 'unterschrieben · Quellen prüfen' : 'unterschrieben' }
        : rvC.state === 'befreiung_ag_offen'
        ? { s: 'PENDING', badge: 'AG-Gegenzeichnung offen' }
        : rvC.state === 'befreiung_an_offen'
        ? { s: 'PENDING', badge: 'MA-Unterschrift offen' }
        : { s: 'WARN',    badge: 'RV-pflichtig · nicht beantragt' })
    : (!rvSigning
        ? { s: 'WARN',    badge: isMinijob ? 'nicht beantragt' : 'nicht erforderlich' }
        : rvSigning.status === 'complete'
        ? { s: 'OK',      badge: 'unterschrieben' }
        : { s: 'PENDING', badge: 'ausstehend' });

  // ── Arbeitszeugnisse ──────────────────────────────────────────────────────
  const zeugnisStatus = zeugnisse.length > 0
    ? { s: 'OK',      badge: `${zeugnisse.length} Zeugnis${zeugnisse.length > 1 ? 'se' : ''}` }
    : { s: 'NEUTRAL', badge: 'keine' };

  // ── Abmahnungen ───────────────────────────────────────────────────────────
  const abmahnungStatus = abmahnungen.length > 0
    ? { s: 'WARN',    badge: `${abmahnungen.length} Abmahnung${abmahnungen.length > 1 ? 'en' : ''}` }
    : { s: 'OK',      badge: 'keine' };

  // ── Kündigung ─────────────────────────────────────────────────────────────
  const kuendigungStatus = kuendigungen.length > 0
    ? { s: 'NEUTRAL', badge: 'Dokument vorhanden' }
    : { s: 'NEUTRAL', badge: 'keine' };

  // ── Dokumente ─────────────────────────────────────────────────────────────
  const dokStatus = !dokSummary
    ? { s: 'NEUTRAL', badge: '–' }
    : dokSummary.total === 0
    ? { s: 'NEUTRAL', badge: 'keine' }
    : dokSummary.pending > 0
    ? { s: 'PENDING', badge: `${dokSummary.pending} Unterschrift${dokSummary.pending > 1 ? 'en' : ''} offen` }
    : { s: 'OK',      badge: `${dokSummary.total} Dokument${dokSummary.total > 1 ? 'e' : ''}` };

  return (
    <Card className="shadow-sm" data-testid="personalakte-overview">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          Personalakte
          <span className="text-xs font-normal text-muted-foreground ml-1">Übersicht</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <AkteRow
          icon={FileText} iconColor="text-primary"
          label="Personalbogen"
          status={bogenStatus.s} badge={bogenStatus.badge}
          sectionKey="personalbogen" onOpenSection={onOpenSection}
        />
        <AkteRow
          icon={Briefcase} iconColor="text-blue-600"
          label="Arbeitsvertrag"
          status={vertragStatus.s} badge={vertragStatus.badge}
          sectionKey="arbeitsvertrag" onOpenSection={onOpenSection}
        />
        {/* "Vertrag erstellen" nur wenn noch kein Vertrag vorhanden */}
        {!signing && onCreateContract && (
          <button
            type="button"
            onClick={onCreateContract}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-blue-300 text-blue-600 text-xs font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            data-testid="personalakte-create-contract-btn">
            <Briefcase className="h-3.5 w-3.5" />
            Vertrag erstellen →
          </button>
        )}
        {isMinijob && (
          <AkteRow
            icon={Shield} iconColor="text-violet-600"
            label="RV-Befreiungsantrag"
            status={rvStatus.s} badge={rvStatus.badge}
            sectionKey="rv" onOpenSection={onOpenSection}
          />
        )}
        <AkteRow
          icon={Award} iconColor="text-violet-500"
          label="Arbeitszeugnisse"
          status={zeugnisStatus.s} badge={zeugnisStatus.badge}
          sectionKey="zeugnisse" onOpenSection={onOpenSection}
        />
        <AkteRow
          icon={AlertOctagon} iconColor="text-amber-600"
          label="Abmahnungen"
          status={abmahnungStatus.s} badge={abmahnungStatus.badge}
          sectionKey="abmahnungen" onOpenSection={onOpenSection}
        />
        <AkteRow
          icon={Scale} iconColor="text-red-600"
          label="Kündigung / Aufhebungsvertrag"
          status={kuendigungStatus.s} badge={kuendigungStatus.badge}
          sectionKey="kuendigung" onOpenSection={onOpenSection}
        />
        <AkteRow
          icon={Folder} iconColor="text-amber-500"
          label="Dokumente"
          status={dokStatus.s} badge={dokStatus.badge}
          sectionKey="dokumente" onOpenSection={onOpenSection}
        />
      </CardContent>
    </Card>
  );
}
